import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Input, InputNumber, Select, Space, Spin, Tabs, theme} from 'antd';
import {MenuOutlined, LeftOutlined, RightOutlined, ZoomInOutlined, ZoomOutOutlined, PrinterOutlined, RotateRightOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {useStorageState} from 'react-storage-hooks';
import {loadPdfLibrary, transformPdfDocumentParams, PDFJS_IMAGE_RESOURCES_PATH} from './pdf/runtime';
import {PdfBookmarks, PdfThumbnails} from './pdf/PdfSidebar';
import {printPdf} from './pdf/printPdf';
import {PdfLocalization} from './pdf/PdfLocalization';
import 'pdfjs-dist/legacy/web/pdf_viewer.css';
import './PdfViewer.css';

export {PDFJS_VERSION, PDFJS_WORKER_URL, transformPdfDocumentParams} from './pdf/runtime';

const french = {
    sidebar: 'Afficher le panneau latéral', previous: 'Page précédente', next: 'Page suivante', page: 'Page',
    zoomOut: 'Zoom arrière', zoomIn: 'Zoom avant', width: 'Largeur de page', fit: 'Page entière',
    search: 'Rechercher', previousMatch: 'Résultat précédent', nextMatch: 'Résultat suivant',
    print: 'Imprimer', cancel: 'Annuler', rotate: 'Tourner', thumbnails: 'Vignettes', bookmarks: 'Signets',
    noBookmarks: 'Aucun signet', loading: 'Chargement du PDF', error: 'Impossible de charger ce PDF.',
    password: 'Mot de passe', unlock: 'Ouvrir', incorrectPassword: 'Mot de passe incorrect.', resize: 'Redimensionner le lecteur PDF',
};

const PdfViewer = ({url, style = {}}) => {
    const {i18n, t} = useTranslation();
    const {token} = theme.useToken();
    const label = (key, fallback) => t(`pdf.${key}`, {defaultValue: i18n.language?.startsWith('fr') ? french[key] : fallback});
    const root = useRef(), container = useRef(), pages = useRef(), session = useRef(), printing = useRef(), drag = useRef();
    const language = useRef(i18n.language), localization = useRef();
    language.current = i18n.language;
    const [height, setHeight] = useStorageState(sessionStorage, 'pdfViewerHeight', 750);
    const [pdf, setPdf] = useState(null), [error, setError] = useState(false);
    const [page, setPage] = useState(1), [zoom, setZoom] = useState('page-width'), [rotation, setRotation] = useState(0);
    const [sidebar, setSidebar] = useState(false), [bookmarks, setBookmarks] = useState([]);
    const [query, setQuery] = useState(''), [matches, setMatches] = useState({current: 0, total: 0});
    const [passwordPrompt, setPasswordPrompt] = useState(null), [password, setPassword] = useState('');
    const [printProgress, setPrintProgress] = useState(null), [bodyHeight, setBodyHeight] = useState(500);

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            setBodyHeight(Math.max(140, entry.contentRect.height - 50));
            const viewer = session.current?.viewer;
            if (!viewer) return;
            if (['page-width', 'page-fit', 'auto'].includes(viewer.currentScaleValue)) {
                viewer.currentScaleValue = viewer.currentScaleValue;
            }
            viewer.update();
        });
        observer.observe(container.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const abort = new AbortController();
        let task, viewer, linkService, l10n;
        setPdf(null); setError(false); setPage(1); setZoom('page-width'); setRotation(0);
        setBookmarks([]); setPasswordPrompt(null); setPassword(''); setQuery(''); setMatches({current: 0, total: 0});
        (async () => {
            const lib = await loadPdfLibrary();
            if (abort.signal.aborted) return;
            const eventBus = new lib.EventBus();
            linkService = new lib.PDFLinkService({eventBus, externalLinkTarget: lib.LinkTarget.BLANK, externalLinkRel: 'noopener noreferrer nofollow'});
            const findController = new lib.PDFFindController({eventBus, linkService});
            l10n = new PdfLocalization(lib.GenericL10n, language.current);
            localization.current = l10n;
            viewer = new lib.PDFViewer({container: container.current, viewer: pages.current, eventBus, linkService,
                findController, l10n, downloadManager: new lib.DownloadManager(), imageResourcesPath: PDFJS_IMAGE_RESOURCES_PATH,
                abortSignal: abort.signal});
            await l10n.translate(container.current);
            if (abort.signal.aborted) return;
            // No PDFScriptingManager: document JavaScript is never executed.
            linkService.setViewer(viewer);
            eventBus.on('pagesinit', () => { viewer.currentScaleValue = 'page-width'; });
            eventBus.on('pagechanging', event => setPage(event.pageNumber));
            eventBus.on('scalechanging', event => setZoom(event.presetValue || String(event.scale)));
            eventBus.on('rotationchanging', event => setRotation(event.pagesRotation));
            eventBus.on('updatefindmatchescount', event => setMatches(event.matchesCount));
            eventBus.on('updatefindcontrolstate', event => setMatches(event.matchesCount));
            eventBus.on('pagerendered', event => { if (event.error) setError(true); });
            session.current = {viewer, linkService, eventBus, abort};
            task = lib.getDocument(transformPdfDocumentParams({url}));
            task.onPassword = (updatePassword, reason) => {
                if (!abort.signal.aborted) setPasswordPrompt({updatePassword, incorrect: reason === lib.PasswordResponses.INCORRECT_PASSWORD});
            };
            const document = await task.promise;
            if (abort.signal.aborted) return;
            linkService.setDocument(document);
            viewer.setDocument(document);
            setPdf(document); setPasswordPrompt(null);
            const outline = await document.getOutline();
            if (!abort.signal.aborted) setBookmarks(outline || []);
        })().catch(() => { if (!abort.signal.aborted) { setPasswordPrompt(null); setError(true); } });
        return () => {
            printing.current?.abort();
            abort.abort();
            if (session.current?.abort === abort) session.current = null;
            if (localization.current === l10n) localization.current = null;
            viewer?.setDocument(null);
            linkService?.setDocument(null);
            l10n?.destroy().catch(() => {});
            task?.destroy().catch(() => {});
        };
    }, [url]);

    useEffect(() => {
        localization.current?.setLanguage(i18n.language).catch(() => {});
    }, [i18n.language]);

    const navigate = number => {
        if (Number.isInteger(number) && number >= 1 && number <= pdf?.numPages && session.current) {
            session.current.viewer.currentPageNumber = number;
        }
    };
    const find = (again = false, previous = false) => session.current?.eventBus.dispatch('find', {
        source: root.current, type: again ? 'again' : '', query, highlightAll: true, caseSensitive: false,
        entireWord: false, findPrevious: previous, matchDiacritics: false,
    });
    const print = async () => {
        if (printing.current) { printing.current.abort(); return; }
        if (!pdf) return;
        const controller = new AbortController();
        printing.current = controller;
        setPrintProgress(0);
        try {
            await printPdf(pdf, {signal: controller.signal, rotation, onProgress: current => setPrintProgress(current)});
        } catch { if (!controller.signal.aborted) setError(true); }
        finally {
            if (printing.current === controller) { printing.current = null; setPrintProgress(null); }
        }
    };
    const zoomOptions = [
        {value: 'page-width', label: label('width', 'Page width')}, {value: 'page-fit', label: label('fit', 'Page fit')},
        ...[0.5, 0.75, 1, 1.25, 1.5, 2].map(value => ({value: String(value), label: `${value * 100}%`})),
    ];
    if (!zoomOptions.some(option => option.value === zoom)) zoomOptions.push({value: zoom, label: `${Math.round(Number(zoom) * 100)}%`});
    const resize = value => setHeight(Math.max(200, Math.min(2400, value)));

    return <div ref={root} className="mg-pdf-root" style={{height, backgroundColor: token.colorBgContainer,
        border: `1px solid ${token.colorBorder}`, '--mg-pdf-border': token.colorBorder, '--mg-pdf-selected': token.colorPrimaryBg, ...style}}
        onKeyDown={event => {
            event.stopPropagation();
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p') { event.preventDefault(); print(); }
        }} onKeyUp={event => event.stopPropagation()}>
        <Space wrap className="mg-pdf-toolbar" size={4}>
            <Button icon={<MenuOutlined/>} aria-label={label('sidebar', 'Toggle sidebar')} aria-pressed={sidebar} onClick={() => setSidebar(!sidebar)}/>
            <Button icon={<LeftOutlined/>} aria-label={label('previous', 'Previous page')} disabled={!pdf || page <= 1} onClick={() => navigate(page - 1)}/>
            <InputNumber aria-label={label('page', 'Page')} min={1} max={pdf?.numPages || 1} precision={0} value={page} disabled={!pdf} onChange={navigate} style={{width: 64}}/>
            <span>/ {pdf?.numPages || 0}</span>
            <Button icon={<RightOutlined/>} aria-label={label('next', 'Next page')} disabled={!pdf || page >= pdf.numPages} onClick={() => navigate(page + 1)}/>
            <Button icon={<ZoomOutOutlined/>} aria-label={label('zoomOut', 'Zoom out')} disabled={!pdf} onClick={() => session.current?.viewer.decreaseScale()}/>
            <Select aria-label="Zoom" value={zoom} options={zoomOptions} style={{width: 150}} disabled={!pdf} onChange={value => { if (session.current) session.current.viewer.currentScaleValue = value; }}/>
            <Button icon={<ZoomInOutlined/>} aria-label={label('zoomIn', 'Zoom in')} disabled={!pdf} onClick={() => session.current?.viewer.increaseScale()}/>
            <Button icon={<RotateRightOutlined/>} aria-label={label('rotate', 'Rotate')} disabled={!pdf} onClick={() => { if (session.current) session.current.viewer.pagesRotation = (session.current.viewer.pagesRotation + 90) % 360; }}/>
            <Button icon={<PrinterOutlined/>} aria-label={printProgress !== null ? label('cancel', 'Cancel') : label('print', 'Print')} disabled={!pdf} onClick={print}/>
            <Input.Search aria-label={label('search', 'Search')} placeholder={label('search', 'Search')} value={query} disabled={!pdf}
                onChange={event => setQuery(event.target.value)} onSearch={() => find()} style={{width: 170}}/>
            <Button icon={<LeftOutlined/>} aria-label={label('previousMatch', 'Previous match')} disabled={!query || !pdf} onClick={() => find(true, true)}/>
            <Button icon={<RightOutlined/>} aria-label={label('nextMatch', 'Next match')} disabled={!query || !pdf} onClick={() => find(true)}/>
            <span aria-live="polite">{matches?.total > 0 && `${matches.current} / ${matches.total}`}</span>
            {printProgress !== null && <span role="status">{label('print', 'Print')}: {printProgress} / {pdf?.numPages}</span>}
        </Space>
        {passwordPrompt && <form className="mg-pdf-password" onSubmit={event => { event.preventDefault(); passwordPrompt.updatePassword(password); setPassword(''); }}>
            {passwordPrompt.incorrect && <Alert type="error" message={label('incorrectPassword', 'Incorrect password.')}/>}
            <Input.Password autoFocus aria-label={label('password', 'Password')} value={password} onChange={event => setPassword(event.target.value)} style={{width: 200}}/>
            <Button htmlType="submit">{label('unlock', 'Unlock')}</Button>
        </form>}
        {error && <Alert type="error" message={label('error', 'Unable to load this PDF.')}/>}
        <div className="mg-pdf-body">
            {sidebar && pdf && <aside className="mg-pdf-sidebar"><Tabs items={[
                {key: 'thumbnails', label: label('thumbnails', 'Thumbnails'), children: <PdfThumbnails pdf={pdf} page={page} rotation={rotation}
                    navigate={navigate} height={bodyHeight} label={number => `${label('page', 'Page')} ${number}`}/>},
                {key: 'bookmarks', label: label('bookmarks', 'Bookmarks'), children: bookmarks.length ?
                    <PdfBookmarks items={bookmarks} linkService={session.current?.linkService}/> : label('noBookmarks', 'No bookmarks')},
            ]}/></aside>}
            <div className="mg-pdf-stage">
                {!pdf && !error && !passwordPrompt && <div className="mg-pdf-loading" role="status"><Spin/>{label('loading', 'Loading PDF')}</div>}
                <div ref={container} className="mg-pdf-scroll"><div ref={pages} className="pdfViewer"/></div>
            </div>
        </div>
        {style.height !== '100%' && <div className="mg-pdf-resize" role="separator" aria-orientation="horizontal" tabIndex={0}
            aria-label={label('resize', 'Resize PDF viewer')} aria-valuenow={height} aria-valuemin={200} aria-valuemax={2400}
            onKeyDown={event => { if (['ArrowUp', 'ArrowDown'].includes(event.key)) { event.preventDefault(); resize(height + (event.key === 'ArrowUp' ? -20 : 20)); } }}
            onPointerDown={event => { drag.current = {y: event.clientY, height}; event.currentTarget.setPointerCapture(event.pointerId); }}
            onPointerMove={event => { if (drag.current) resize(drag.current.height + event.clientY - drag.current.y); }}
            onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}/>}
    </div>;
};

export default PdfViewer;
